import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownLeft,
  Building2,
  Clock,
  ExternalLink,
  Loader2,
  Sparkles,
  Sun,
  Sunrise,
  Sunset,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";
import { formatMoneyMinor } from "@/lib/money";
import { PageLoader } from "@/components/brand/PageLoader";

type Currency = string;

interface MatchAgg {
  id: string;
  status: string;
  format: string;
  target_players: number;
  currency: Currency;
  confirmed_at: string | null;
  starts_at: string | null;
  court_id: string;
  court_name: string;
  participants: { status: string; unit_price_minor: number }[];
}

interface CourtRow {
  id: string;
  name: string;
}

interface Payout {
  id: string;
  amount: number;
  currency: string;
  arrival_date: number;
  status: string;
  failure_message: string | null;
}

/**
 * Venue → Analytics. The metrics venue partners use to decide whether
 * Whistll is earning its keep - bookings, revenue, when their courts
 * actually run, average fill, court-by-court utilisation, and payouts.
 *
 * Data is venue-scoped via RLS (venue_admins can only see their own
 * venue's matches/courts/availability). Payouts come from the
 * stripe-venue-payouts Edge Function which acts on the venue's
 * Connect account.
 */
export function VenueAnalytics() {
  const { profile } = useAuth();
  const [courts, setCourts] = useState<CourtRow[] | null>(null);
  const [matches, setMatches] = useState<MatchAgg[] | null>(null);
  const [slotsCount, setSlotsCount] = useState<number | null>(null);
  const [payouts, setPayouts] = useState<Payout[] | null>(null);
  const [payoutsErr, setPayoutsErr] = useState<string | null>(null);
  const [stripeNotConnected, setStripeNotConnected] = useState(false);
  const [currency, setCurrency] = useState<Currency>("GBP");

  useEffect(() => {
    if (!profile?.venue_application_id) return;
    let cancelled = false;
    (async () => {
      // Find this venue's courts
      const { data: courtRows } = await supabase
        .from("courts")
        .select("id, name, currency")
        .eq("venue_application_id", profile.venue_application_id);
      if (cancelled) return;
      const _courts = (courtRows ?? []) as Array<CourtRow & { currency: Currency }>;
      setCourts(_courts);
      if (_courts[0]) setCurrency(_courts[0].currency);
      const ids = _courts.map((c) => c.id);
      if (ids.length === 0) {
        setMatches([]);
        setSlotsCount(0);
        return;
      }

      // Pull all matches for these courts + participants + start time
      const { data: matchRows } = await supabase
        .from("matches")
        .select(
          `id, status, format, target_players, currency, confirmed_at,
           court_id,
           availability_slot:slot_id ( starts_at ),
           court:court_id ( name ),
           participants:match_participants ( status, unit_price_minor )`,
        )
        .in("court_id", ids);
      if (cancelled) return;
      const m = ((matchRows ?? []) as any[]).map(
        (r) =>
          ({
            id: r.id,
            status: r.status,
            format: r.format,
            target_players: r.target_players,
            currency: r.currency,
            confirmed_at: r.confirmed_at,
            starts_at: r.availability_slot?.starts_at ?? null,
            court_id: r.court_id,
            court_name: r.court?.name ?? "-",
            participants: r.participants ?? [],
          }) satisfies MatchAgg,
      );
      setMatches(m);

      // Total available slots (denominator for utilisation). Cap at recent
      // 4 weeks so we measure recent capacity, not all-time.
      const fourWeeksAgo = new Date();
      fourWeeksAgo.setDate(fourWeeksAgo.getDate() - 28);
      const { count: slots } = await supabase
        .from("availability_slots")
        .select("id", { count: "exact", head: true })
        .in("court_id", ids)
        .gte("starts_at", fourWeeksAgo.toISOString());
      if (cancelled) return;
      setSlotsCount(slots ?? 0);
    })();

    // Pull payouts via the Edge Function
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) return;
        const res = await fetch(
          `${env.supabaseUrl}/functions/v1/stripe-venue-payouts`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "content-type": "application/json",
            },
            body: JSON.stringify({ limit: 8 }),
          },
        );
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok) {
          setPayoutsErr(body.error ?? `Stripe ${res.status}`);
          return;
        }
        if (body.not_yet_connected) {
          setStripeNotConnected(true);
          setPayouts([]);
          return;
        }
        setPayouts(body.payouts ?? []);
      } catch (e) {
        if (!cancelled) {
          setPayoutsErr(e instanceof Error ? e.message : "Network error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profile?.venue_application_id]);

  const k = courts && matches && slotsCount !== null
    ? compute(courts, matches, slotsCount, currency)
    : null;

  return (
    <div>
      <header className="mb-8">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner · Analytics
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          What Whistll is bringing you - bookings, revenue, when your
          courts actually run, and how full each one runs.
        </p>
      </header>

      {!k ? (
        <PageLoader label="Crunching…" />
      ) : (
        <>
          {/* Headline tiles */}
          <section className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              icon={<Building2 className="h-4 w-4" />}
              label="Bookings · this month"
              value={k.monthlyBookings}
              tone="volt"
              sub={
                k.monthlyBookings === 0
                  ? "First month - runs land here."
                  : `${k.allTimeConfirmed} all-time`
              }
            />
            <Stat
              icon={<Wallet className="h-4 w-4" />}
              label="Revenue · this month"
              value={formatMoneyMinor(k.monthlyRevenueMinor, k.currency)}
              tone="volt"
              sub={
                k.monthlyRevenueMinor === 0
                  ? "Court-fee revenue - the 20% Whistll fee sits on top"
                  : `${formatMoneyMinor(k.allTimeRevenueMinor, k.currency)} all-time`
              }
            />
            <Stat
              icon={<Users className="h-4 w-4" />}
              label="Avg players / booking"
              value={
                k.avgPlayers === null ? "-" : k.avgPlayers.toFixed(1)
              }
              sub={
                k.avgPlayers === null
                  ? "Need ≥ 1 confirmed run"
                  : `${k.capturedSeats} captured seats`
              }
            />
            <Stat
              icon={<TrendingUp className="h-4 w-4" />}
              label="Court utilisation · 4w"
              value={
                k.utilisationPct === null
                  ? "-"
                  : `${k.utilisationPct}%`
              }
              sub={
                k.utilisationPct === null
                  ? "Need at least one upcoming slot"
                  : `${k.confirmedRecent} of ${k.slotsCount} slots ran`
              }
            />
          </section>

          {/* Most popular time slots */}
          <Card
            title="Most popular time slots"
            icon={<Clock className="h-4 w-4" />}
          >
            {k.slotMix.length === 0 ? (
              <EmptyNote>
                Once your first matches confirm, we'll show which time
                bands fill fastest - useful for tuning slot supply.
              </EmptyNote>
            ) : (
              <ul className="flex flex-col gap-2">
                {k.slotMix.map(([band, count]) => (
                  <BreakdownRow
                    key={band}
                    label={band}
                    icon={timeBandIcon(band)}
                    count={count}
                    total={k.allTimeConfirmed}
                  />
                ))}
              </ul>
            )}
          </Card>

          {/* Per-court utilisation */}
          <Card
            title="Per-court utilisation"
            icon={<Building2 className="h-4 w-4" />}
            className="mt-4"
          >
            {k.perCourt.length === 0 ? (
              <EmptyNote>
                Add your first court in the Courts tab to start tracking.
              </EmptyNote>
            ) : (
              <ul className="flex flex-col gap-2">
                {k.perCourt.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {c.confirmed} confirmed run
                        {c.confirmed === 1 ? "" : "s"} ·{" "}
                        {c.captured} captured seats
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">
                        {formatMoneyMinor(c.revenueMinor, k.currency)}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        court-fee revenue
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Payouts */}
          <Card
            title="Pending payouts and payout history"
            icon={<Wallet className="h-4 w-4" />}
            className="mt-4"
          >
            {payoutsErr ? (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{payoutsErr}</span>
              </div>
            ) : stripeNotConnected ? (
              <div className="flex items-start gap-3 rounded-md border border-dashed border-border bg-card/40 p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">
                    Stripe isn't connected yet
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Until Stripe is connected we can't show payouts -
                    finish onboarding on the Payouts tab.
                  </p>
                  <Link
                    to="../payouts"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-volt hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Go to Payouts
                  </Link>
                </div>
              </div>
            ) : payouts === null ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading
                Stripe payouts…
              </div>
            ) : payouts.length === 0 ? (
              <EmptyNote>
                No payouts yet. The first one lands the moment a
                confirmed game's funds clear Stripe.
              </EmptyNote>
            ) : (
              <ul className="flex flex-col gap-2">
                {payouts.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-3">
                      <ArrowDownLeft className="h-3.5 w-3.5 text-volt" />
                      <div>
                        <p className="font-bold">
                          {formatMoneyMinor(p.amount, p.currency)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Arrives{" "}
                          {new Date(
                            p.arrival_date * 1000,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <PayoutBadge status={p.status} failure={p.failure_message} />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Retention nudge - gentle reminder of what these numbers
              mean for the relationship. */}
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-volt/30 bg-volt-subtle p-4">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-volt" />
            <div className="text-sm">
              <p className="font-semibold">Where Whistll is moving for you</p>
              <p className="mt-1 text-muted-foreground">
                These numbers are court-fee revenue you wouldn't have had
                without the Whistll demand pipeline. The 20% platform fee
                sits on top of your court price - you keep 100% of the
                court fee, every time.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Compute
// ----------------------------------------------------------------------------

function compute(
  courts: CourtRow[],
  matches: MatchAgg[],
  slotsCount: number,
  currency: Currency,
) {
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const fourWeeksAgo = Date.now() - 28 * 24 * 60 * 60 * 1000;

  let monthlyBookings = 0;
  let monthlyRevenueMinor = 0;
  let allTimeConfirmed = 0;
  let allTimeRevenueMinor = 0;
  let capturedSeats = 0;
  let confirmedRecent = 0;

  // Time-band tally for popular slots
  const slotMixCounts = new Map<string, number>();

  // Per-court rollup
  const perCourtMap = new Map<
    string,
    { id: string; name: string; confirmed: number; captured: number; revenueMinor: number }
  >();
  for (const c of courts) {
    perCourtMap.set(c.id, {
      id: c.id,
      name: c.name,
      confirmed: 0,
      captured: 0,
      revenueMinor: 0,
    });
  }

  for (const m of matches) {
    const isConfirmed =
      m.status === "confirmed" || m.status === "completed";
    if (!isConfirmed) continue;

    allTimeConfirmed++;

    const captured = m.participants.filter((p) => p.status === "captured");
    capturedSeats += captured.length;
    const revenue = captured.reduce(
      (sum, p) => sum + p.unit_price_minor,
      0,
    );
    allTimeRevenueMinor += revenue;

    const isThisMonth =
      m.confirmed_at && new Date(m.confirmed_at) >= monthStart;
    if (isThisMonth) {
      monthlyBookings++;
      monthlyRevenueMinor += revenue;
    }

    // Recent (last 4w) confirmed for utilisation
    if (
      m.confirmed_at &&
      new Date(m.confirmed_at).getTime() >= fourWeeksAgo
    ) {
      confirmedRecent++;
    }

    // Time band (based on slot start, falling back to confirmed_at)
    const startIso = m.starts_at ?? m.confirmed_at;
    if (startIso) {
      const hour = new Date(startIso).getHours();
      const band = timeBand(hour);
      slotMixCounts.set(band, (slotMixCounts.get(band) ?? 0) + 1);
    }

    // Per-court rollup
    const pc = perCourtMap.get(m.court_id);
    if (pc) {
      pc.confirmed++;
      pc.captured += captured.length;
      pc.revenueMinor += revenue;
    }
  }

  const avgPlayers =
    allTimeConfirmed > 0 ? capturedSeats / allTimeConfirmed : null;
  const utilisationPct =
    slotsCount > 0
      ? Math.round((confirmedRecent / slotsCount) * 100)
      : null;

  // Sort time bands by display order, not just count, so morning is
  // morning regardless of count
  const BAND_ORDER = ["Morning", "Lunch", "Afternoon", "Evening", "Night"];
  const slotMix = BAND_ORDER.filter((b) => slotMixCounts.has(b)).map(
    (b) => [b, slotMixCounts.get(b)!] as [string, number],
  );

  const perCourt = [...perCourtMap.values()].sort(
    (a, b) => b.confirmed - a.confirmed,
  );

  return {
    currency,
    monthlyBookings,
    monthlyRevenueMinor,
    allTimeConfirmed,
    allTimeRevenueMinor,
    capturedSeats,
    avgPlayers,
    confirmedRecent,
    slotsCount,
    utilisationPct,
    slotMix,
    perCourt,
  };
}

function timeBand(hour: number): string {
  if (hour < 6) return "Night";
  if (hour < 11) return "Morning";
  if (hour < 14) return "Lunch";
  if (hour < 17) return "Afternoon";
  if (hour < 22) return "Evening";
  return "Night";
}

function timeBandIcon(band: string) {
  switch (band) {
    case "Morning":
      return <Sunrise className="h-3.5 w-3.5 text-volt" />;
    case "Lunch":
    case "Afternoon":
      return <Sun className="h-3.5 w-3.5 text-volt" />;
    case "Evening":
      return <Sunset className="h-3.5 w-3.5 text-volt" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-volt" />;
  }
}

// ----------------------------------------------------------------------------
// UI primitives
// ----------------------------------------------------------------------------

function Stat({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  tone?: "volt";
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span className={tone === "volt" ? "text-volt" : undefined}>{icon}</span>
        {label}
      </div>
      <p
        className={
          "text-2xl font-black tracking-tight " +
          (tone === "volt" ? "text-volt" : "")
        }
      >
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          {sub}
        </p>
      )}
    </div>
  );
}

function Card({
  title,
  icon,
  children,
  className,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        "rounded-2xl border border-border bg-card p-5 " + (className ?? "")
      }
    >
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function BreakdownRow({
  label,
  count,
  total,
  icon,
}: {
  label: string;
  count: number;
  total: number;
  icon?: React.ReactNode;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <li>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 font-semibold">
          {icon}
          {label}
        </span>
        <span className="text-muted-foreground">
          {count} · {pct}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-volt"
          style={{ width: `${pct}%` }}
        />
      </div>
    </li>
  );
}

function PayoutBadge({
  status,
  failure,
}: {
  status: string;
  failure: string | null;
}) {
  const tone =
    status === "paid"
      ? { color: "#4ade80", bg: "rgba(74,222,128,0.12)" }
      : status === "in_transit" || status === "pending"
        ? { color: "#fbbf24", bg: "rgba(251,191,36,0.12)" }
        : status === "failed" || status === "canceled"
          ? { color: "#f87171", bg: "rgba(248,113,113,0.12)" }
          : { color: "#8899A6", bg: "rgba(136,153,166,0.12)" };
  return (
    <div
      className="rounded-full border px-2 py-0.5 text-[10px] font-bold"
      style={{ color: tone.color, borderColor: `${tone.color}55`, background: tone.bg }}
      title={failure ?? status}
    >
      {status.replace("_", " ")}
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="py-6 text-center text-xs text-muted-foreground">{children}</p>
  );
}
